import { useField, useFormikContext } from 'formik';
import { memo, useRef, useCallback, useEffect } from 'react';
import { ErrorLevel } from '../../../../../API/Enums';
import { FormSelectProps } from './FormSelect.types';
import { SelectProps } from '../Select/Select.types';
import { GMSelect } from '../..';

/**
 * Select for use in a Formik.
 *
 * Sets the value, error, onChange & helperText internally
 */
const FormSelect: React.FC<FormSelectProps> = memo(({
    validateOnChange,
    validateBeforeSubmit,
    onChange: propsOnChange,
    onBlur: propsOnBlur,
    helperText,
    warningText,
    ...props
}) => {
    const [field, meta] = useField(props.name);

    // we don't use helpers above since those references change every render
    const { validateField, setFieldValue, setFieldTouched, submitCount } = useFormikContext();
    const prevValueRef = useRef<any>(field.value);

    const onChange = useCallback((value: string) => {
        if (propsOnChange) {
            propsOnChange(value);
        }
        else {
            setFieldValue(props.name, value);
        }
    }, [props.name, propsOnChange, setFieldValue]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setFieldTouched(props.name, true);
        if (propsOnBlur) {
            propsOnBlur(e);
        }
    }, [propsOnBlur, setFieldTouched, props.name]);

    useEffect(() => {
        if (validateOnChange && meta.touched && field.value !== prevValueRef.current) {
            validateField(props.name);
        }

        prevValueRef.current = field.value;
    }, [field.value, meta.touched, props.name, validateOnChange, validateField]);

    const getErrorLevel = (): ErrorLevel => {
        if (!!meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return ErrorLevel.Error;
        }

        if (!!warningText) {
            return ErrorLevel.Warning;
        }

        return ErrorLevel.None;
    };

    const getHelperText = (): SelectProps['helperText'] => {
        if (meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return meta.error;
        }

        if (warningText) {
            return warningText;
        }

        return helperText;
    };

    return (
        <GMSelect
            value={field.value?.toString()}
            onChange={onChange}
            error={getErrorLevel()}
            helperText={getHelperText()}
            onBlur={onBlur}
            {...props}
        />
    );
});

export default FormSelect;